/**
 * HrCostForecastPrint — impression du rapport Planning « Personnel cost
 * forecast ». Réutilise rolling12Baseline + movementImpactOnCell de
 * HrCostSimulationPage. Fenêtre = année civile sélectionnée (Jan → Déc),
 * et non la fenêtre glissante de l'écran (choix adapté à un rapport annuel).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listHrContracts, peopleCostSynthesis, listPayrollMovements, listHrLppRates } from '../../../api/hr';
import { rolling12Baseline, movementImpactOnCell } from '../HrCostSimulationPage';
import { namedMovementDelta } from '../../treasury/engine/computePayrollFromHr';
import { extraMonths } from '../salaryMass';
import { formatNumber } from '../../../utils/format';
import PrintReportHeader from './PrintReportHeader';
import './PeoplePrint.css';

const f0 = (v) => formatNumber(v, 0);

export default function HrCostForecastPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id;
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    Promise.all([
      listHrContracts(tenantId).then(r => r.data || []),
      peopleCostSynthesis(tenantId, year, 'all').then(r => { if (r?.error) throw new Error(r.error); return r.data || r; }),
      listPayrollMovements(tenantId).then(r => r.data || []),
      listHrLppRates(tenantId).then(r => r.data || []),
    ]).then(([contracts, synth, movements, lppRates]) => {
      if (!alive) return;
      setData({ contracts: contracts || [], synth, movements: movements || [], lppRates: lppRates || [] });
      if (onDataReady) onDataReady('personnel-cost-forecast');
    }).catch(() => { if (onDataReady) onDataReady('personnel-cost-forecast'); });
    return () => { alive = false; };
  }, [tenantId, year]); // eslint-disable-line

  const model = useMemo(() => {
    if (!data) return null;
    // Fenêtre = année civile : Janvier → Décembre de `year`.
    const extra = extraMonths(activeTenant);
    const baseline = rolling12Baseline(data.contracts, year, 0, extra);
    const eff = baseline.map(c => {
      let v = c.base;
      for (const mv of data.movements) {
        if (!mv.person_id) v += movementImpactOnCell(mv, c.year, c.monthIdx);
      }
      v += namedMovementDelta(data.contracts, data.movements, c.year, c.monthIdx, extra);
      return { ...c, value: v };
    });
    const totalBase = baseline.reduce((a, c) => a + c.base, 0);
    const totalEff  = eff.reduce((a, c) => a + c.value, 0);
    const impact    = totalEff - totalBase;
    const csEmprRate = Number(data.synth?.cs_fallback_rate_empr || 0);
    const csEmplRate = Number(data.synth?.cs_fallback_rate_empl || 0);
    const lppEmplPct = (() => {
      if (!data.lppRates.length) return 0;
      const sumRef = data.lppRates.reduce((a, r) => a + Number(r.reference_salary || 0), 0);
      const sumEmpl = data.lppRates.reduce((a, r) => a + Number(r.lpp_employee_chf || 0), 0);
      return sumRef > 0 ? (sumEmpl / sumRef) * 100 : 0;
    })();
    const netFactor = 1 - (csEmplRate + lppEmplPct) / 100;
    const totalCost = totalEff * (1 + csEmprRate / 100);
    return { eff, totalBase, totalEff, impact, csEmprRate, netFactor, totalCost, totalNet: totalEff * netFactor };
  }, [data, year, activeTenant?.thirteenth_salary_month, activeTenant?.thirteenth_salary_month_2, activeTenant?.fourteenth_salary_month_13, activeTenant?.fourteenth_salary_month]);

  if (!model) return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;

  const { eff, totalBase, totalEff, impact, csEmprRate, netFactor, totalCost, totalNet } = model;

  return (
    <section className="ppl-print__section ppl-print__section--wide">
      <PrintReportHeader category="Planning" title="Personnel cost forecast" year={year} tenant={activeTenant} editedAt={editedAt} />

      <table className="ppl-print__table" style={{ marginBottom: 12 }}>
        <tbody>
          <tr>
            <td style={{ textAlign: 'left' }}>Gross salaries (12M)</td>
            <td><strong>{f0(totalEff)}</strong></td>
            <td style={{ textAlign: 'left' }}>Total employer cost (+{formatNumber(csEmprRate, 1)}% SC)</td>
            <td><strong>{f0(totalCost)}</strong></td>
            <td style={{ textAlign: 'left' }}>Δ vs baseline</td>
            <td><strong>{impact >= 0 ? '+' : ''}{f0(impact)}</strong></td>
          </tr>
        </tbody>
      </table>

      <table className="ppl-print__table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Month</th>
            <th>Baseline gross</th>
            <th>Movements Δ</th>
            <th>Effective gross</th>
            <th>Net</th>
          </tr>
        </thead>
        <tbody>
          {eff.map((c, i) => {
            const delta = c.value - c.base;
            return (
              <tr key={i}>
                <td style={{ textAlign: 'left' }}>{t(`common:months.${c.monthIdx + 1}`)} {c.year}</td>
                <td>{f0(c.base)}</td>
                <td style={{ color: delta ? (delta > 0 ? '#5C7A3E' : '#BA5D4A') : undefined }}>{delta ? `${delta > 0 ? '+' : ''}${f0(delta)}` : '—'}</td>
                <td>{f0(c.value)}</td>
                <td>{f0(c.value * netFactor)}</td>
              </tr>
            );
          })}
          <tr>
            <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
            <td><strong>{f0(totalBase)}</strong></td>
            <td><strong>{impact ? `${impact > 0 ? '+' : ''}${f0(impact)}` : '—'}</strong></td>
            <td><strong>{f0(totalEff)}</strong></td>
            <td><strong>{f0(totalNet)}</strong></td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}
